import { DataListItemDirective, DataListDetailDirective } from '../../../shared/ui/data-list/data-list-item.directive';
import { WorkspaceViewDirective } from '../../../components/workspace-view/workspace-view.directive';
import { DataListComponent } from '../../../shared/ui/data-list/data-list.component';
import { DataPage, DataQuery } from '../../../models/data-source';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { BrIconComponent } from '../../../shared/ui/icon/icon.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import {
  catchError,
  concatMap,
  EMPTY,
  finalize,
  from,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  toArray,
} from 'rxjs';
import {
  DataFilterComponent,
  DataFilterField,
  DataFilterValues,
} from '../../../shared/ui/data-filter/data-filter.component';
import { DataTableColumn, DataTableComponent } from '../../../shared/ui/data-table/data-table.component';
import { DataTableCellDirective } from '../../../shared/ui/data-table/data-table-cell.directive';
import { ConfirmationDialogService } from '../../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import {
  dynamicApiErrorMessage,
  DynamicFrameworkApiService,
} from './dynamic-framework-api.service';
import {
  DynamicColumn,
  DynamicFieldValue,
  DynamicWorkflowDefinition,
  DynamicWorkflowInstanceListItem,
} from './dynamic-framework.models';
import {
  SmartWorkflowImportDialogComponent,
  SmartWorkflowImportDialogData,
} from './smart-workflow-import-dialog.component';
import {
  SmartWorkflowRecordDialogComponent,
  SmartWorkflowRecordDialogData,
} from './smart-workflow-record-dialog.component';
import { listWorkflowFields, listWorkflowRecordFields } from './dynamic-workflow-fields';
import { toRuleParams } from '../../../shared/ui/data-filter/data-filter.rules';
import { smartWorkflowRuleFilterFields, workflowFieldColumnId } from './smart-workflow-filters';
import { triggerBlobDownload } from '../../../shared/download.util';

type WorkflowTableRow = DynamicWorkflowInstanceListItem & Record<string, unknown>;

@Component({
  selector: 'app-smart-workflow-workspace',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    BrIconComponent,
    MatTooltipModule,
    DataFilterComponent,
    DataTableComponent, DataListComponent, DataListItemDirective, DataListDetailDirective, WorkspaceViewDirective,
    DataTableCellDirective,
  ],
  templateUrl: './smart-workflow-workspace.component.html',
  styleUrls: ['./smart-workflow-workspace.component.css'],
})
export class SmartWorkflowWorkspaceComponent implements OnInit, OnDestroy {
  @ViewChild('compactList') compactList?: DataListComponent<WorkflowTableRow>;

  @ViewChild(DataTableComponent)
  table?: DataTableComponent<WorkflowTableRow>;

  workflow?: DynamicWorkflowDefinition;
  columns: DataTableColumn[] = [];
  filterFields: DataFilterField[] = [];
  filterValues: DataFilterValues = {};
  /**
   * The rule half of the filter bar, kept so the fetch sends only these as
   * rules: the pinned node filter has a query parameter of its own.
   */
  private ruleFields: DataFilterField[] = [];
  loading = true;
  errorMessage = '';
  importing = false;
  exporting = false;
  readonly deletingRecordIds = new Set<number>();

  readonly workflowKey: string;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    route: ActivatedRoute,
    private readonly api: DynamicFrameworkApiService,
    private readonly confirmation: ConfirmationDialogService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
  ) {
    this.workflowKey = route.snapshot.paramMap.get('workflowKey') ?? '';
  }

  ngOnInit(): void {
    this.loadWorkflow();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  fetch = (query: DataQuery): Observable<DataPage<WorkflowTableRow>> =>
    this.api.listWorkflowInstances(this.workflowKey, {
      nodeKey: this.nodeKey(query.filters),
      rules: toRuleParams(query.filters, this.ruleFields),
      sort: query.sort || undefined,
      desc: query.desc,
      page: query.page,
      pageSize: query.pageSize,
    }).pipe(map(page => ({
      total: page.total,
      items: page.items.map(item => this.toTableRow(item)),
    })));

  applyFilters(values: DataFilterValues): void {
    this.filterValues = values;
  }

  addRecord(): void {
    const fields = this.recordCreationFields();
    if (!fields) return;
    this.dialog.open<
      SmartWorkflowRecordDialogComponent,
      SmartWorkflowRecordDialogData,
      DynamicFieldValue[]
    >(SmartWorkflowRecordDialogComponent, {
      data: { title: 'Add record', fields },
      width: '640px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'br-dialog-panel',
    }).afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(values => {
      if (!values) return;
      this.api.createWorkflowRecord(this.workflowKey, values)
        .pipe(takeUntil(this.destroyed$)).subscribe({
          next: () => {
            this.snackBar.open('Record added.', undefined, { duration: 2500 });
            this.table?.refresh();
            this.compactList?.reload();
          },
          error: error => this.showError(error, 'Could not add the record.'),
        });
    });
  }

  importCsv(): void {
    const fields = this.recordCreationFields();
    if (!fields || !this.workflow) return;
    this.dialog.open<
      SmartWorkflowImportDialogComponent,
      SmartWorkflowImportDialogData,
      DynamicFieldValue[][]
    >(SmartWorkflowImportDialogComponent, {
      data: { workflowLabel: this.workflow.label, fields },
      width: '680px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'br-dialog-panel',
    }).afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(rows => {
      if (!rows?.length) return;
      this.importRows(rows);
    });
  }

  exportCsv(): void {
    if (this.exporting) return;
    this.exporting = true;
    this.api.exportWorkflowRecords(this.workflowKey)
      .pipe(takeUntil(this.destroyed$)).subscribe({
        next: blob => {
          this.exporting = false;
          triggerBlobDownload(blob, `${this.workflowKey}.csv`);
        },
        error: error => {
          this.exporting = false;
          this.showError(error, 'Could not export the workflow data.');
        },
      });
  }

  editRecord(row: WorkflowTableRow): void {
    const fields = this.workflowFields(row.currentNodeKey);
    if (!fields) return;
    this.dialog.open<
      SmartWorkflowRecordDialogComponent,
      SmartWorkflowRecordDialogData,
      DynamicFieldValue[]
    >(SmartWorkflowRecordDialogComponent, {
      data: { title: 'Edit record', fields, row: row.currentRow },
      width: '640px',
      maxWidth: 'calc(100vw - 32px)',
      panelClass: 'br-dialog-panel',
    }).afterClosed().pipe(takeUntil(this.destroyed$)).subscribe(values => {
      if (!values) return;
      this.api.updateWorkflowCurrentRow(
        this.workflowKey,
        row.id,
        row.currentRow.version,
        values,
      ).pipe(takeUntil(this.destroyed$)).subscribe({
        next: () => {
          this.snackBar.open('Record updated.', undefined, { duration: 2500 });
          this.table?.refresh([row]);
          this.compactList?.reload();
        },
        error: error => this.showError(error, 'Could not update the record.'),
      });
    });
  }

  deleteRecord(row: WorkflowTableRow): void {
    if (this.deletingRecordIds.has(row.id)) return;
    this.confirmation.confirm({
      title: 'Delete record?',
      message: `Record ${row.id} will be permanently deleted.`,
      confirmLabel: 'Delete',
      danger: true,
    }).pipe(
      switchMap(confirmed => {
        if (!confirmed) return EMPTY;
        this.deletingRecordIds.add(row.id);
        return this.api.deleteWorkflowRecord(
          this.workflowKey,
          row.id,
          row.version,
          row.currentRow.version,
        ).pipe(finalize(() => this.deletingRecordIds.delete(row.id)));
      }),
      takeUntil(this.destroyed$),
    ).subscribe({
      next: () => {
        this.snackBar.open('Record deleted.', undefined, { duration: 2500 });
        this.table?.refresh();
        this.compactList?.reload();
      },
      error: error => this.showError(error, 'Could not delete the record.'),
    });
  }

  private loadWorkflow(): void {
    this.loading = true;
    this.errorMessage = '';
    this.api.getWorkflow(this.workflowKey).pipe(takeUntil(this.destroyed$)).subscribe({
      next: workflow => {
        this.workflow = workflow;
        if (workflow.status !== 'published') {
          this.errorMessage = 'This workflow is not published.';
        } else {
          this.configureTable(workflow);
        }
        this.loading = false;
      },
      error: error => {
        this.errorMessage = dynamicApiErrorMessage(error, 'Could not load the workflow.');
        this.loading = false;
      },
    });
  }

  private configureTable(workflow: DynamicWorkflowDefinition): void {
    const fields = listWorkflowFields(workflow.nodes, true);
    this.columns = [
      { id: 'id', label: 'ID', sortable: false },
      { id: 'currentNodeLabel', label: 'Node', sortKey: 'currentNode' },
      ...fields.map(field => ({
        id: workflowFieldColumnId(field.key),
        label: field.label,
        sortable: false as const,
      })),
      { id: 'updatedAt', label: 'Updated', align: 'end' as const },
    ];
    this.ruleFields = smartWorkflowRuleFilterFields(fields);
    this.filterFields = [
      // Pinned, and not a rule: the node keeps its own query parameter.
      {
        id: 'nodeKey',
        label: 'Node',
        allLabel: 'All nodes',
        options: workflow.nodes.map(node => ({ value: node.key, label: node.label })),
      },
      ...this.ruleFields,
    ];
  }

  private toTableRow(item: DynamicWorkflowInstanceListItem): WorkflowTableRow {
    const row: WorkflowTableRow = { ...item };
    const fields = new Map(listWorkflowFields(this.workflow?.nodes ?? [], true)
      .map(field => [field.key, field]));
    item.currentRow.values.forEach(value => {
      row[workflowFieldColumnId(value.columnKey)] =
        this.displayValue(fields.get(value.columnKey), value);
    });
    return row;
  }

  private displayValue(field: DynamicColumn | undefined, value: DynamicFieldValue): unknown {
    if (field?.dataType === 'choice') {
      return field.options.find(option => option.key === value.value)?.label ?? value.value;
    }
    if (field?.dataType === 'boolean') return value.value ? 'Yes' : 'No';
    return value.value;
  }

  private recordCreationFields(): DynamicColumn[] | null {
    return this.workflowFields(this.workflow?.startNodeKey);
  }

  private workflowFields(requiredNodeKey: string | null | undefined): DynamicColumn[] | null {
    if (!requiredNodeKey || !this.workflow) {
      this.snackBar.open('Workflow fields are unavailable.', 'OK', { duration: 5000 });
      return null;
    }
    return listWorkflowRecordFields(this.workflow.nodes, requiredNodeKey);
  }

  private nodeKey(filters: DataFilterValues): string | undefined {
    const value = filters['nodeKey'];
    return typeof value === 'string' && value ? value : undefined;
  }

  private importRows(rows: DynamicFieldValue[][]): void {
    this.importing = true;
    from(rows).pipe(
      concatMap(values => this.api.createWorkflowRecord(this.workflowKey, values).pipe(
        map(() => true),
        catchError(() => of(false)),
      )),
      toArray(),
      takeUntil(this.destroyed$),
    ).subscribe(results => {
      this.importing = false;
      const failed = results.filter(result => !result).length;
      const added = results.length - failed;
      this.snackBar.open(
        failed ? `${added} records added; ${failed} failed.` : `${added} records added.`,
        failed ? 'OK' : undefined,
        { duration: failed ? 6000 : 2500 },
      );
      if (added) {
        this.table?.refresh();
        this.compactList?.reload();
      }
    });
  }

  private showError(error: unknown, fallback: string): void {
    this.snackBar.open(dynamicApiErrorMessage(error, fallback), 'OK', { duration: 6000 });
  }
}
